/* eslint-disable no-case-declarations */
import { areaCodes } from '../areaCode';

export const formatter = (
  value: string | number | undefined,
  type: 'dni' | 'cuit' | 'currency' | 'capitalize' | 'date' | 'number' | 'phone',
  minimumFractions?: number,
) => {
  if (!value && value !== 0) return '';
  const stringValue = `${value}`;
  switch (type) {
    case 'capitalize':
      return (
        stringValue
          ?.replaceAll('_', ' ')
          .split(' ')
          .map((s: string) => s.charAt(0).toUpperCase() + s.substring(1).toLowerCase())
          .join(' ') || ''
      );
    case 'currency':
      if (!stringValue) return '';
      const amount = Number(stringValue.replace(',', '.'));
      // Cents only when there are any, always two digits: "$ 45.450" or "$ 45.450,50", never "$ 1.234,5"
      const centsDigits = Math.round(amount * 100) % 100 === 0 ? 0 : 2;
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: centsDigits,
        maximumFractionDigits: centsDigits,
      }).format(amount);
    case 'number':
      return stringValue === '0'
        ? '0,00'
        : stringValue
          ? new Intl.NumberFormat('es-AR', { minimumFractionDigits: minimumFractions || 0 }).format(
              Number(stringValue.replace(',', '.')),
            )
          : '';
    case 'dni':
      return stringValue ? Number(stringValue).toLocaleString().replaceAll(',', '.') : '';
    case 'cuit':
      if (!stringValue) return '';
      if (stringValue.length <= 2) {
        return stringValue;
      } else if (stringValue.length <= 10) {
        return `${stringValue.slice(0, 2)}-${stringValue.slice(2)}`;
      } else {
        return `${stringValue.slice(0, 2)}-${stringValue.slice(2, 10)}-${stringValue.slice(10)}`;
      }
    case 'date':
      if (!stringValue) return '';
      const isEpoch = /^\d{10}$|^\d{13}$/.test(stringValue);
      const dateValue = isEpoch
        ? new Date(Number(stringValue.length === 10 ? `${stringValue}000` : stringValue))
        : new Date(stringValue.length <= 10 ? `${stringValue} 00:00:00` : stringValue);
      return dateValue.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    case 'phone':
      const areaCode = areaCodes.find((code) => code === stringValue.slice(0, code.length));
      const areaCodeLength = areaCode?.length || 0;
      return areaCode
        ? `${areaCode} ${stringValue.slice(areaCodeLength, areaCodeLength + 4)} ${stringValue.slice(areaCodeLength + 4)}`
        : stringValue;
  }
};

/**
 * DNI as typed, digits only. Same rule on login and password recovery, web and mobile, so a document that logs in is
 * never rejected while recovering the password. 7 to 10 digits, the range login has always accepted.
 */
export const isValidDni = (documentNumber: string) => /^\d{7,10}$/.test(documentNumber);

/**
 * Splits the customer token's "LASTNAME, FIRSTNAMES" full name into a greeting name and a "Firstnames Lastname" display name.
 * Without a comma the parts can't be told apart, so the first word is taken as the first name.
 */
export const splitFullName = (rawFullName?: string) => {
  const [lastName = '', firstNames = ''] = (rawFullName ?? '')
    .split(',')
    .map((part) => formatter(part.trim().replace(/\s+/g, ' '), 'capitalize'));
  if (!firstNames) {
    return { firstName: lastName.split(' ')[0] ?? '', fullName: lastName };
  }
  return { firstName: firstNames.split(' ')[0] ?? '', fullName: `${firstNames} ${lastName}`.trim() };
};
